import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { config } from '@/config';
import { useActiveContextReady } from './useActiveContextReady';
import type {
  BulkCustomerResult,
  MspCustomer,
  MspCustomerNotes,
  MspCustomerCandidate,
  MspCustomerDetail,
  MspCustomerIncident,
  MspVocabulary,
} from '@/types/mspCustomer';

/**
 * The MSP customer surface.
 *
 * **Import these through `@/features/msp`, not from here.** This module names
 * `/api/msp`, which the trial-bundle check forbids in the shipped JS; the
 * feature barrel is alias-swapped to a no-op teaser in trial builds, so a core
 * page importing through it stays trial-safe. See `vite.edition.ts` and
 * `scripts/check-trial-bundle.mjs`.
 */

const backendApi = config.backendApi;

/**
 * The organization's customers.
 *
 * Its own query key, deliberately not shared with `['msp-divisions', orgId]`:
 * that key already returns a differently-shaped array (keyed `id`, carrying
 * metrics and health), and reusing it would hand one shape to code expecting
 * the other.
 */
export interface MspCustomerList {
  customers: MspCustomer[];
  vocabulary: MspVocabulary;
}

const EMPTY_VOCABULARY: MspVocabulary = { lifecycle: [], tier: [] };

export function useMspCustomers(
  organizationId: string | null | undefined,
  enabled = true,
  { includeMetrics = false }: { includeMetrics?: boolean } = {},
) {
  const contextReady = useActiveContextReady();
  return useQuery<MspCustomerList>({
    // Org id in the key, matching `useDivisions` and `useMSPDivisions`. An MSP
    // tech can switch into a customer's context without a reload, and an
    // unscoped key would serve the previous org's customers until a background
    // refetch landed.
    // The rollup is part of the key: a cached list fetched without metrics must
    // not satisfy a caller that needs them.
    queryKey: ['msp-customers', organizationId, includeMetrics],
    queryFn: async () => {
      const response = await axios.get<Partial<MspCustomerList>>(`${backendApi}/api/msp/customers`, {
        params: includeMetrics ? { include: 'metrics' } : undefined,
        withCredentials: true,
      });
      return {
        customers: response.data.customers ?? [],
        vocabulary: response.data.vocabulary ?? EMPTY_VOCABULARY,
      };
    },
    enabled: enabled && contextReady && !!organizationId,
  });
}

/** One page of not-yet-customer divisions, plus the full total. */
export interface MspCustomerCandidateList {
  candidates: MspCustomerCandidate[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Divisions that are not yet customers, likely ones first, one page at a time.
 *
 * Paged rather than fetched whole: an org with hundreds of divisions should not
 * ship them all to a picker. The offset is part of the query key, and the
 * previous page is kept on screen while the next loads so paging doesn't flash
 * empty.
 */
export function useMspCustomerCandidates(
  organizationId: string | null | undefined,
  enabled = true,
  { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {},
) {
  const contextReady = useActiveContextReady();
  return useQuery<MspCustomerCandidateList>({
    queryKey: ['msp-customer-candidates', organizationId, limit, offset],
    queryFn: async () => {
      const response = await axios.get<Partial<MspCustomerCandidateList>>(
        `${backendApi}/api/msp/customers/candidates`,
        { params: { limit, offset }, withCredentials: true },
      );
      return {
        candidates: response.data.candidates ?? [],
        total: response.data.total ?? 0,
        limit,
        offset,
      };
    },
    placeholderData: keepPreviousData,
    enabled: enabled && contextReady && !!organizationId,
  });
}

/**
 * One customer, with everything its own page needs, in a single request.
 *
 * A distinct key from `['msp-customers', …]` rather than a slice of it: the two
 * return different shapes, and the standing rule here is that one key means one
 * shape. The list row is used as placeholder data instead, so the page paints
 * immediately with numbers that are at worst one fetch stale, and corrects
 * itself the moment the detail request lands.
 */
export function useMspCustomer(
  divisionId: string | null | undefined,
  organizationId: string | null | undefined,
  enabled = true,
) {
  const contextReady = useActiveContextReady();
  const queryClient = useQueryClient();
  return useQuery<MspCustomerDetail>({
    queryKey: ['msp-customer', organizationId, divisionId],
    queryFn: async () => {
      const response = await axios.get<MspCustomerDetail>(`${backendApi}/api/msp/customers/${divisionId ?? ''}`, {
        // Always the same facets, so this key always yields the same shape.
        params: { include: 'metrics,regions,gateways,tags' },
        withCredentials: true,
      });
      return response.data;
    },
    placeholderData: () => {
      const cached = queryClient.getQueryData<MspCustomerList>(['msp-customers', organizationId, true]);
      const row = cached?.customers.find((c) => c.divisionId === divisionId);
      if (!row) return undefined;
      // The facets genuinely aren't known yet — left undefined rather than
      // filled with empty arrays, which would render "no gateways" as a fact.
      return { customer: row, vocabulary: cached?.vocabulary ?? EMPTY_VOCABULARY, administratorsTeamId: null };
    },
    enabled: enabled && contextReady && !!organizationId && !!divisionId,
  });
}

/**
 * One customer's open incidents, worst first.
 *
 * Its own request rather than a facet, so it can reload on its own and carry its
 * own limit — and its own refusal, which is why the page can still render the
 * rest of a customer when this alone is denied.
 */
export function useMspCustomerIncidents(
  divisionId: string | null | undefined,
  organizationId: string | null | undefined,
  enabled = true,
  limit = 20,
) {
  const contextReady = useActiveContextReady();
  return useQuery<MspCustomerIncident[]>({
    queryKey: ['msp-customer-incidents', organizationId, divisionId, limit],
    queryFn: async () => {
      const response = await axios.get<{ incidents: MspCustomerIncident[] }>(
        `${backendApi}/api/msp/customers/${divisionId ?? ''}/incidents`,
        { params: { limit }, withCredentials: true },
      );
      return response.data.incidents;
    },
    enabled: enabled && contextReady && !!organizationId && !!divisionId,
  });
}

/**
 * Invalidates the queries carrying a customer's *state* — the rollup the board
 * sorts on, the detail page, and both incident feeds.
 *
 * Deliberately narrower than [`useInvalidateCustomerLists`]: a device going
 * unreachable changes what the board *says*, never *who is on it*, so the roster
 * queries (candidates, divisions) are left alone. This is the one used for
 * realtime events, where the difference is the difference between one query per
 * update and four.
 */
export function useInvalidateCustomerState() {
  const queryClient = useQueryClient();
  // Stable across renders: the WebSocket sync schedules it from long-lived handlers.
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['msp-customers'] });
    void queryClient.invalidateQueries({ queryKey: ['msp-customer'] });
    void queryClient.invalidateQueries({ queryKey: ['msp-customer-incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['msp-incident-feed'] });
  }, [queryClient]);
}

/** Invalidates every list whose contents depend on who is a customer. */
function useInvalidateCustomerLists() {
  const queryClient = useQueryClient();
  const invalidateState = useInvalidateCustomerState();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['msp-customer-candidates'] });
    void queryClient.invalidateQueries({ queryKey: ['msp-divisions'] });
    // Who is a customer also changes what every state surface reports — the
    // detail page and its incident feed read the same facts as the list, so if an
    // edit refreshed only the list, the page the edit was made *on* would be the
    // one surface still showing the old value.
    invalidateState();
  };
}

export function useDesignateCustomer() {
  const invalidate = useInvalidateCustomerLists();
  return useMutation<MspCustomer, Error, { divisionId: string; lifecycle?: string }>({
    mutationFn: async ({ divisionId, lifecycle }) => {
      const response = await axios.post<{ customer: MspCustomer }>(
        `${backendApi}/api/msp/customers`,
        { divisionId, lifecycle },
        { withCredentials: true },
      );
      return response.data.customer;
    },
    onSuccess: invalidate,
  });
}

export function useUndesignateCustomer() {
  const invalidate = useInvalidateCustomerLists();
  return useMutation<{ deletedCount: number }, Error, string>({
    mutationFn: async (divisionId) => {
      const response = await axios.delete<{ deletedCount: number }>(
        `${backendApi}/api/msp/customers/${divisionId}`,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: invalidate,
  });
}

export function useBulkDesignateCustomers() {
  const invalidate = useInvalidateCustomerLists();
  return useMutation<BulkCustomerResult, Error, { divisionIds: string[]; lifecycle?: string }>({
    mutationFn: async (body) => {
      const response = await axios.post<BulkCustomerResult>(`${backendApi}/api/msp/customers/bulk`, body, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Open incidents across every customer, worst first.
 *
 * Server-ordered and server-attributed on purpose: an incident belongs to the
 * customer whose division it names, or failing that whose region it sits in, and
 * that rule lives in one place rather than being re-derived here.
 */
export function useMspIncidentFeed(organizationId: string | null | undefined, enabled = true, limit = 20) {
  const contextReady = useActiveContextReady();
  return useQuery<MspCustomerIncident[]>({
    queryKey: ['msp-incident-feed', organizationId, limit],
    queryFn: async () => {
      const response = await axios.get<{ incidents: MspCustomerIncident[] }>(
        `${backendApi}/api/msp/customers/incidents`,
        { params: { limit }, withCredentials: true },
      );
      return response.data.incidents;
    },
    enabled: enabled && contextReady && !!organizationId,
  });
}

/** Changes a customer's lifecycle, tier, or account manager. */
export function useUpdateCustomer() {
  const invalidate = useInvalidateCustomerLists();
  return useMutation<
    MspCustomer,
    Error,
    { divisionId: string; lifecycle?: string; tier?: string; accountManager?: string }
  >({
    mutationFn: async ({ divisionId, ...fields }) => {
      const response = await axios.patch<{ customer: MspCustomer }>(
        `${backendApi}/api/msp/customers/${divisionId}`,
        fields,
        { withCredentials: true },
      );
      return response.data.customer;
    },
    onSuccess: invalidate,
  });
}

/**
 * Sets a customer's notes.
 *
 * A single markdown document, replaced wholesale — no partial-update subtlety.
 * An empty string is a valid blank note, not a delete.
 */
export function useUpdateCustomerNotes() {
  const invalidate = useInvalidateCustomerLists();
  return useMutation<MspCustomerNotes, Error, { divisionId: string; content: string }>({
    mutationFn: async ({ divisionId, content }) => {
      const response = await axios.put<MspCustomerNotes>(
        `${backendApi}/api/msp/customers/${divisionId}/notes`,
        { content },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Invites someone to administer a customer.
 *
 * The customer's team is resolved server-side; the caller supplies an email and
 * nothing else. Doing it here would need `team:read` and `context:read`, which
 * an MSP's commercial staff have no reason to hold.
 */
export function useInviteCustomerAdmin() {
  const queryClient = useQueryClient();
  return useMutation<
    { invitationId: string; email: string; teamId: string; teamName: string; expiresAt: string },
    Error,
    { divisionId: string; email: string }
  >({
    mutationFn: async ({ divisionId, email }) => {
      const response = await axios.post<{
        invitationId: string;
        email: string;
        teamId: string;
        teamName: string;
        expiresAt: string;
      }>(`${backendApi}/api/msp/customers/${divisionId}/invite`, { email }, { withCredentials: true });
      return response.data;
    },
    onSuccess: () => {
      // The pending-invitation list is what stops a second invite being offered
      // to an address the backend will reject as a duplicate.
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

/** Un-designates several divisions at once — the undo for a mis-aimed batch. */
export function useBulkUndesignateCustomers() {
  const invalidate = useInvalidateCustomerLists();
  return useMutation<BulkCustomerResult, Error, { divisionIds: string[] }>({
    mutationFn: async (body) => {
      const response = await axios.delete<BulkCustomerResult>(`${backendApi}/api/msp/customers/bulk`, {
        data: body,
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: invalidate,
  });
}
